<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getRandomBg } from "../utils/randomBg";
import { usersVideoTracks } from "../states/usersVideoStreams";
import { authState } from "../states/auth";
import useClickOutside from "../composables/useClickOutside";
import CrownIcon from "./icons/CrownIcon.vue";
import { userMap, usersState } from "../states/usersState";
import { isAdmin } from "../states/adminsState";
import { cameraTrack, desiredMediaState } from "../states/media/state";

const props = defineProps<{
  userId: string;
  avatar?: string;
  displayedName: string;
  size: {
    width: number;
    height: number;
  };
}>();

const isVideo = computed(() => {
  if(props.userId === authState.id) {
    return desiredMediaState.cameraEnabled && !!cameraTrack.value
  }
  const user = usersState.users.find((u) => u.id === props.userId);
  return user?.camera && !!usersVideoTracks[props.userId];
});

const videoTrack = computed(() => {
  if(props.userId === authState.id) {
    return cameraTrack.value
  }
  return usersVideoTracks[props.userId]
})

const videoEl = ref<HTMLVideoElement | null>(null);
watch(
  () => {
    return [videoEl.value, videoTrack.value];
  },
  () => {
    if (videoEl.value && videoTrack.value) {
      videoTrack.value.attach(videoEl.value);
    }
  },
);

const tile_info = computed(() => {
  const res = {
    name: "",
    avatar: props.avatar,
  };
  if (props.displayedName) {
    const nf = props.displayedName.split(" ");
    res.name += nf[0][0].toUpperCase();
    if (nf[1] && nf[1].length > 0) {
      res.name += nf[1][0].toUpperCase();
    }
  }
  return res;
});

defineExpose({
  videoEl,
});

const avatarSize = computed(() => {
  return {
    width: props.size.height / 2 + "px",
    height: props.size.height / 2 + "px",
  };
});

const opened = ref(false);
const popup = ref<HTMLElement | null>(null);
useClickOutside(popup, opened);
</script>
<template>
  <div class="custom-video" :style="{
    width: `${size.width}px`,
    height: `${size.height}px`,
  }">
    <video v-if="isVideo" class="videoEl" :class="{
      reverse: userId === authState.id
    }" ref="videoEl" :width="size.width" :height="size.height" autoplay
      :controls="false" />
    <div v-else class="image-preview" :style="{
      width: `${size.width}px`,
      height: `${size.height}px`,
    }">
      <div class="preview-avatar" :style="{
        backgroundColor:
          tile_info.name && !tile_info.avatar
            ? getRandomBg(tile_info.name)
            : '#A4A4A4',
        ...avatarSize,
      }">
        <img v-if="tile_info.avatar" class="covered" :style="avatarSize" :src="tile_info.avatar" alt="" />
        <span v-else-if="tile_info.name" class="covered name-char">{{
          tile_info.name
        }}</span>
        <img v-else src="@images/default_avatar.svg" class="covered" />
      </div>
    </div>
    <div class="info" :class="{
      opened
    }">
      <div class="content">
        <CrownIcon v-if="isAdmin(userId)" />
        <span class="name">{{ displayedName }}</span>
        <svg v-if="!userMap[userId]?.micro" width="20" height="20" viewBox="0 0 20 20" fill="none"
          xmlns="http://www.w3.org/2000/svg" :style="{
            flexShrink: 0,
          }">
          <path
            d="M4.99967 8.5V9.25C4.99967 10.6424 5.52646 11.9777 6.46414 12.9623C7.40182 13.9469 8.67359 14.5 9.99967 14.5M9.99967 14.5C11.3258 14.5 12.5975 13.9469 13.5352 12.9623C13.5702 12.9256 13.6045 12.8884 13.6384 12.8508M9.99967 14.5V17.5M14.9997 8.5V9.25C14.9997 9.74091 14.9342 10.2247 14.8083 10.6886M9.99967 17.5H8.74967M9.99967 17.5H11.2497M3.33301 2.5L11.5747 10.7756M16.6663 15.9166L13.6384 12.8508M7.85682 7.04241V9.25C7.85682 10.4926 8.81621 11.5 9.99967 11.5C10.6225 11.5 11.1832 11.221 11.5747 10.7756M11.5747 10.7756L13.277 12.4849L13.6384 12.8508M12.1425 7.91667V4.75C12.1425 3.50736 11.1831 2.5 9.99967 2.5C9.14116 2.5 8.40056 3.03011 8.05859 3.7956"
            stroke="#A4A4A4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
    </div>
  </div>
</template>
<style scoped>
.custom-video {
  overflow: hidden;
  border-radius: 8px;
  height: 100%;
  width: 100%;
  position: relative;
}

.info {
  height: 36px;
  width: 100%;
  position: absolute;
  bottom: 12px;
  display: flex;
  justify-content: center;
  z-index: 15;
}

.control-item:hover {
  background-color: #494949;
}

.info .content {
  height: 100%;
  max-width: 80%;
  border-radius: 8px;
  background: rgba(41, 41, 41, 0.85);

  backdrop-filter: blur(2px);
  padding: 8px 16px;
  display: inline-flex;
  padding: 4px 12px 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.info .content .name {
  color: #ececec;

  text-align: center;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

video {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.image-preview {
  display: flex;
  border-radius: 8px;
  background-color: #747474;
  align-items: center;
  justify-content: center;
}

.preview-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.preview-avatar img {
  height: 100%;
}

.name-char {
  font-size: 30px;
  color: #FFFFFF;
  text-align: center;
}

.videoEl {
  background: #292929;
}

video.reverse {
  transform: scaleX(-1);
}
</style>
